import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Card,
  CardContent,
  Chip,
  Box
} from '@mui/material';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { AdviseEngagement, AdviseCustomer } from '../../../types/advise';
import { getAllProductTypes } from '../../../constants/advise/productTypes';

interface EngagementBuilderDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (engagement: Partial<AdviseEngagement>) => Promise<AdviseEngagement | null>;
  engagement: AdviseEngagement;
  customers: AdviseCustomer[];
}

interface FormData {
  engagement_name: string;
  customer_id: string;
  product_type: string;
}

export const EngagementBuilderDialog = ({
  open,
  onClose,
  onSave,
  engagement,
  customers
}: EngagementBuilderDialogProps) => {
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState<FormData>({
    engagement_name: '',
    customer_id: '',
    product_type: ''
  });

  useEffect(() => {
    if (open) {
      setForm({
        engagement_name: engagement.engagement_name || '',
        customer_id: engagement.customer_id || '',
        product_type: engagement.product_type || ''
      });
    }
  }, [open, engagement]);

  const productTypes = useMemo(() => getAllProductTypes(), []);

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === form.customer_id),
    [customers, form.customer_id]
  );

  const handleChange = (field: keyof FormData, value: unknown) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    const payload: Partial<AdviseEngagement> = {
      engagement_name: form.engagement_name,
      customer_id: form.customer_id,
      product_type: form.product_type
    };

    try {
      const result = await onSave(payload);
      if (result) onClose();
    } finally {
      setSaving(false);
    }
  };

  const canSave = form.engagement_name.trim() !== '' && form.customer_id !== '' && form.product_type !== '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth>
      <DialogTitle>Edit Engagement</DialogTitle>
      <DialogContent dividers>
        <Box className="flex flex-col gap-4">
          <TextField
            label="Engagement Name"
            value={form.engagement_name}
            onChange={(e) => handleChange('engagement_name', e.target.value)}
            fullWidth
            required
          />

          <FormControl
            fullWidth
            required>
            <InputLabel>Product Type</InputLabel>
            <Select
              value={form.product_type}
              label="Product Type"
              onChange={(e) => handleChange('product_type', e.target.value)}>
              {productTypes.map((pt) => (
                <MenuItem
                  key={pt.id}
                  value={pt.id}>
                  {pt.name} — {pt.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Customer"
            value={selectedCustomer?.company_name || ''}
            disabled
          />

          {selectedCustomer && (
            <Card variant="outlined">
              <CardContent>
                <Typography
                  variant="subtitle2"
                  gutterBottom>
                  Customer Profile
                </Typography>
                <Box className="flex flex-wrap gap-2">
                  {selectedCustomer.industry && (
                    <Chip
                      label={selectedCustomer.industry}
                      size="small"
                    />
                  )}
                  {selectedCustomer.company_size && (
                    <Chip
                      label={`${selectedCustomer.company_size} employees`}
                      size="small"
                    />
                  )}
                  {selectedCustomer.budget_range && (
                    <Chip
                      label={selectedCustomer.budget_range}
                      size="small"
                    />
                  )}
                  {selectedCustomer.num_agents > 0 && (
                    <Chip
                      label={`${selectedCustomer.num_agents} agents`}
                      size="small"
                    />
                  )}
                  {selectedCustomer.timeline && (
                    <Chip
                      label={selectedCustomer.timeline}
                      size="small"
                    />
                  )}
                </Box>
                {selectedCustomer.priority_requirements?.length > 0 && (
                  <Box className="mt-2">
                    <Typography
                      variant="caption"
                      color="textSecondary">
                      Priority Requirements
                    </Typography>
                    <Box className="flex flex-wrap gap-1 mt-1">
                      {selectedCustomer.priority_requirements.map((req) => (
                        <Chip
                          key={req}
                          label={req}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <button
          type="button"
          className={DEFAULT_CANCEL_BUTTON_STYLE}
          onClick={onClose}
          disabled={saving}>
          Cancel
        </button>
        <button
          type="button"
          className={DEFAULT_ADVANCE_BUTTON_STYLE}
          onClick={handleSubmit}
          disabled={!canSave || saving}>
          {saving ? <ComponentSpinner sx={{ height: '19px' }} /> : 'Save Changes'}
        </button>
      </DialogActions>
    </Dialog>
  );
};
